import { useMemo } from 'react';
import type { FormField } from '@/features/application/types/forms';
import { CrmMappingInput } from '@/features/form-builder/components/CrmMappingInput';
import { useBankAnalysisMappingStore } from '@/features/form-builder/store/bank-analysis-mapping.store';
import { useBankAnalysisCrmMapping } from '@/features/form-builder/hooks/use-bank-analysis-crm-mapping.hook';

interface BankAnalysisMetricEditorPaneProps {
  className?: string;
}

// Purpose: Right-pane editor for a selected bank analysis metric mapping.
const BankAnalysisMetricEditorPane = ({ className = '' }: BankAnalysisMetricEditorPaneProps) => {
  const { activeMetricSelection, metricFieldsByKey } = useBankAnalysisMappingStore();
  const sourceIndex = activeMetricSelection?.sourceIndex;
  const periodIndex = typeof sourceIndex === 'number' ? sourceIndex : null;

  const metricField: FormField | null = useMemo(() => {
    if (!activeMetricSelection) return null;
    const key = `${activeMetricSelection.metricId}::${
      Number.isInteger(activeMetricSelection.sourceIndex) ? activeMetricSelection.sourceIndex : 'na'
    }`;
    return metricFieldsByKey[key] || null;
  }, [activeMetricSelection, metricFieldsByKey]);

  if (!metricField || !activeMetricSelection) {
    return (
      <div className={className}>
        <div className="border rounded-md bg-white p-6 text-gray-600">
          Select a bank analysis metric to edit its CRM mapping.
        </div>
      </div>
    );
  }

  const { getMappingSummary, ...crmMappingProps } = useBankAnalysisCrmMapping(
    metricField,
    activeMetricSelection,
    true
  );

  return (
    <div className={className}>
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-800">
          {metricField.label}
          {periodIndex !== null && (
            <span className="text-sm font-normal text-gray-500">
              {' '}
              (Period {periodIndex + 1})
            </span>
          )}
        </h3>
        <p className="text-sm text-gray-500">{getMappingSummary()}</p>
      </div>
      <div className="bg-white border rounded-md">
        <CrmMappingInput
          key={metricField.fieldApiName || metricField.id}
          field={metricField}
          {...(crmMappingProps as any)}
        />
      </div>
    </div>
  );
};

export default BankAnalysisMetricEditorPane;
